// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary, ChartDataWithMetricType, EcosystemMetricValues, ChartData } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import type { EcosystemMetricCardData } from './ecosystem-influence.entity';

/**
 * Configuration interface for BoardMembers entity
 */
type BoardMembersConfig = BaseMetricConfig;

/**
 * BoardMembers Entity - Encapsulates all board member-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the board members ecosystem metric.
 */
export class BoardMembers extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'board_members' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: BoardMembersConfig = {
    metricType: 'board_members',
    display: {
      displayName: 'Board Members',
      chartType: 'bar' as ChartType,
      tooltip: {
        definition: 'Are any foundation board members employed by our company?',
        scoringRules: [
          '0 board members = <b>Silent</b> (0 points)',
          '1 or more board members = <b>Leading</b> (1 point)'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company does not employ any board members for {context}.',
      },
      positiveValue: {
        descriptionTemplate: 'Our company employs {count} board member{plural} for {context}.',
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for board members
   */
  static getBottomSummary(value: number, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const boardMemberCount = value || 0;
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    if (boardMemberCount === 0) {
      const description = this.formatDescription(
        this.CONFIG.bottomSummary.zeroValue.description,
        { context: contextText }
      );

      return {
        title: this.CONFIG.display.displayName,
        description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        count: boardMemberCount.toString(),
        plural: boardMemberCount > 1 ? 's' : '',
        context: contextText
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

  private static determineChartDataPresence(
    boardMembersCount: number
  ): boolean {
    return boardMembersCount > 0;
  }

  private static calculateIconType(
    boardMembersCount: number,
    allTimeTotalBoardMembers: number,
    isLfProject: boolean
  ): 'eyes' | 'no-signal' | undefined {
    const neverUsedLfxTools = allTimeTotalBoardMembers === 0 || isLfProject === false;
    
    if (neverUsedLfxTools) {
      return 'no-signal';
    }
    
    if (boardMembersCount === 0) {
      return 'eyes';
    }
    
    return undefined;
  }

  private static buildMultiSeriesChartData(
    chartData: ChartData[]
  ): { data: number[]; label?: string; dashArray?: number[] }[] {
    return [
      {
        data: chartData.map(d => d.metricCount),
        label: 'Our Organization'
      },
      {
        data: chartData.map(d => d.othersCount),
        dashArray: [2, 2],
        label: 'Others'
      }
    ];
  }

  private static buildNoDataCard(
    baseCard: Partial<EcosystemMetricCardData>,
    allTimeTotal: number
  ): EcosystemMetricCardData {
    return {
      ...baseCard as EcosystemMetricCardData,
      data: [],
      multiSeriesData: [],
      xAxisLabels: [],
      firstDate: 'No data',
      lastDate: 'No data',
      isProjectLevelChart: false,
      isFoundationLevelChart: true,
      companyHasActivity: false,
      projectHasActivity: false,
      projectChoosesNotToUseTool: allTimeTotal === 0
    };
  }

  public static transformToCardData(
    metric: ChartDataWithMetricType,
    metricValues: EcosystemMetricValues | undefined,
    isLfProject: boolean,
    projectType: string | null | undefined,
    projectName: string | null | undefined,
    foundationName: string | null | undefined
  ): EcosystemMetricCardData {
    const config = this.getConfig();
    const boardMembersCount = metricValues?.boardMembersCount ?? 0;
    const allTimeTotalBoardMembers = metricValues?.allTimeTotalBoardMembers ?? 0;
    const level = metricValues?.boardMembersLevel as ScoreLevel;

    const hasData = this.determineChartDataPresence(boardMembersCount);
    const actualChartType = hasData ? config.display.chartType : 'no-data';
    
    const iconType = this.calculateIconType(boardMembersCount, allTimeTotalBoardMembers, isLfProject);
    const bottomSummary = this.getBottomSummary(boardMembersCount, allTimeTotalBoardMembers, isLfProject, level, projectType);

    const baseCard = {
      title: config.display.displayName,
      titleDescription: BaseMetricEntity.generateRulesHtml(config.display.tooltip),
      chartType: actualChartType,
      firstDate: metric.firstDate,
      lastDate: metric.lastDate,
      bottomSummary,
      metricType: 'board_members' as const,
      iconType,
      header: foundationName,
      headerVariant: 'neutral' as 'neutral' | 'accent'
    };

    if (!hasData) {
      return this.buildNoDataCard(baseCard, allTimeTotalBoardMembers);
    }

    const xAxisLabels = KeyProjectsHelper.generateXAxisLabels(metric.chartData);
    const multiSeriesData = this.buildMultiSeriesChartData(metric.chartData);

    // Calculate sorting properties
    const cumulativeMetricCount = metric.chartData.reduce((acc, curr) => acc + curr.metricCount, 0);
    const cumulativeOthersCount = metric.chartData.reduce((acc, curr) => acc + curr.othersCount, 0);
    const companyHasActivity = cumulativeMetricCount > 0;
    const projectHasActivity = cumulativeMetricCount > 0 || cumulativeOthersCount > 0;
    const projectChoosesNotToUseTool = allTimeTotalBoardMembers === 0;

    return {
      ...baseCard,
      data: metric.chartData.map(d => d.metricCount),
      xAxisLabels,
      multiSeriesData,
      isProjectLevelChart: false,
      isFoundationLevelChart: true,
      companyHasActivity,
      projectHasActivity,
      projectChoosesNotToUseTool
    };
  }

  public static getIconTypeForChartData(_chartData: {metricCount: number; othersCount: number}[]): 'eyes' | undefined {
    return undefined;
  }
}
